import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as ManageIcon,
  FitnessCenter as ExerciseIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import { TrainingProgramService, WorkoutSessionService, SetService, ExerciseService, ProgramCatalogService, ApiError } from '../../services';
import { DataAdapter, type FrontendTrainingProgram } from '../../services/dataAdapter';
import type { Exercise, TrainingProgram, ProgramCatalogProgram } from '../../services/types';
import { ProgramBuilderV2 } from './ProgramBuilderV2';
import { ExerciseManager } from '../exercises/ExerciseManager';
import RichTextEditor from '../common/RichTextEditor';

const TrainingPrograms: React.FC = () => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<FrontendTrainingProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [managingProgram, setManagingProgram] = useState<TrainingProgram | null>(null);
  const [showExerciseManager, setShowExerciseManager] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProgramId, setEditingProgramId] = useState<number | null>(null);
  const [editProgramName, setEditProgramName] = useState("");
  const [editProgramDescription, setEditProgramDescription] = useState("");
  const [editProgramIntroduction, setEditProgramIntroduction] = useState("");
  const [editProgramDuration, setEditProgramDuration] = useState(4);
  const [editProgramWorkoutsPerWeek, setEditProgramWorkoutsPerWeek] = useState(3);
  const [editProgramDifficulty, setEditProgramDifficulty] = useState("beginner");
  const [editProgramGoal, setEditProgramGoal] = useState("general_fitness");
  const [newProgramName, setNewProgramName] = useState("");
  const [newProgramDescription, setNewProgramDescription] = useState("");
  const [newProgramIntroduction, setNewProgramIntroduction] = useState("");
  const [newProgramDuration, setNewProgramDuration] = useState(4);
  const [newProgramWorkoutsPerWeek, setNewProgramWorkoutsPerWeek] = useState(3);
  const [newProgramDifficulty, setNewProgramDifficulty] = useState("beginner");
  const [newProgramGoal, setNewProgramGoal] = useState("general_fitness");
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogPrograms, setCatalogPrograms] = useState<ProgramCatalogProgram[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogGoalFilter, setCatalogGoalFilter] = useState("all");
  const [importingSlug, setImportingSlug] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Load data on component mount
  useEffect(() => {
    loadPrograms();
    loadExercises();
  }, []);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load training programs
      const backendPrograms = await TrainingProgramService.getAll();
      
      // Load related data for each program
      const programsWithData = await Promise.all(
        backendPrograms.map(async (program) => {
          const workoutSessions = await WorkoutSessionService.getByTrainingProgram(program.id);
          const allSets = await Promise.all(
            workoutSessions.map(session => SetService.getByWorkoutSession(session.id))
          );
          const flatSets = allSets.flat();
          
          return DataAdapter.toFrontendTrainingProgram(program, workoutSessions, flatSets, exercises);
        })
      );
      
      setPrograms(programsWithData);
    } catch (err) {
      console.error('Error loading programs:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to load training programs');
    } finally {
      setLoading(false);
    }
  };

  const loadExercises = async () => {
    try {
      const exerciseList = await ExerciseService.getAll();
      setExercises(exerciseList);
    } catch (err) {
      console.error('Error loading exercises:', err);
    }
  };

  const loadCatalogPrograms = async () => {
    try {
      setCatalogLoading(true);
      setCatalogError(null);
      const catalog = await ProgramCatalogService.getAll();
      setCatalogPrograms(catalog);
    } catch (err) {
      console.error('Error loading program catalog:', err);
      setCatalogError(err instanceof ApiError ? err.message : 'Failed to load program catalog');
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleOpenCatalog = async () => {
    setCatalogDialogOpen(true);
    if (catalogPrograms.length === 0) {
      await loadCatalogPrograms();
    }
  };

  const handleImportCatalogProgram = async (slug: string) => {
    try {
      setImportingSlug(slug);
      setError(null);
      setSuccessMessage(null);

      const result = await ProgramCatalogService.importProgram(slug);
      await loadPrograms();

      setSuccessMessage(
        `${result.programName} added to your programs (${result.addedExercises} exercises imported)`
      );
    } catch (err) {
      console.error('Error importing program:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to import program from catalog');
    } finally {
      setImportingSlug(null);
    }
  };

  const handleCreateProgram = async () => {
    if (!newProgramName.trim()) return;
    
    try {
      setError(null);
      
      const programData = DataAdapter.toBackendTrainingProgram({
        name: newProgramName,
        description: newProgramDescription,
        introduction: newProgramIntroduction,
        durationWeeks: newProgramDuration,
        workoutsPerWeek: newProgramWorkoutsPerWeek,
        difficulty: newProgramDifficulty,
        goal: newProgramGoal,
        active: false,
      });
      
      const newBackendProgram = await TrainingProgramService.create(programData);
      const newFrontendProgram = DataAdapter.toFrontendTrainingProgram(newBackendProgram, [], [], exercises);
      
      setPrograms([...programs, newFrontendProgram]);
      setDialogOpen(false);
      resetDialogForm();
    } catch (err) {
      console.error('Error creating program:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to create program');
    }
  };

  const handleDeleteProgram = async (programId: number) => {
    if (!window.confirm('Are you sure you want to delete this training program? This action cannot be undone.')) {
      return;
    }
    
    try {
      setError(null);
      await TrainingProgramService.delete(programId);
      setPrograms(programs.filter(p => p.id !== programId));
    } catch (err) {
      console.error('Error deleting program:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to delete program');
    }
  };

  const handleStartEditProgram = (program: FrontendTrainingProgram) => {
    setEditingProgramId(program.id);
    setEditProgramName(program.name);
    setEditProgramDescription(program.description || '');
    setEditProgramIntroduction(program.introduction || '');
    setEditProgramDuration(program.durationWeeks);
    setEditProgramWorkoutsPerWeek(program.workoutsPerWeek);
    setEditProgramDifficulty(program.difficulty);
    setEditProgramGoal(program.goal);
  };

  const handleSaveEditProgram = async () => {
    if (!editingProgramId || !editProgramName.trim()) return;

    try {
      setError(null);

      const updateData = {
        name: editProgramName,
        description: editProgramDescription || undefined,
        introduction: editProgramIntroduction || undefined,
        durationWeeks: editProgramDuration,
        workoutsPerWeek: editProgramWorkoutsPerWeek,
        difficulty: editProgramDifficulty,
        goal: editProgramGoal,
      };

      await TrainingProgramService.update(editingProgramId, updateData);

      // Update local state
      setPrograms(programs.map(p => {
        if (p.id === editingProgramId) {
          return {
            ...p,
            name: editProgramName,
            description: editProgramDescription || undefined,
            introduction: editProgramIntroduction || undefined,
            durationWeeks: editProgramDuration,
            workoutsPerWeek: editProgramWorkoutsPerWeek,
            difficulty: editProgramDifficulty,
            goal: editProgramGoal,
          };
        }
        return p;
      }));

      setEditingProgramId(null);
    } catch (err) {
      console.error('Error updating program:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to update program');
    }
  };

  const handleCancelEditProgram = () => {
    setEditingProgramId(null);
  };

  const handleToggleArchive = async (program: FrontendTrainingProgram) => {
    try {
      setError(null);
      const newActive = !program.active;
      await TrainingProgramService.setActive(program.id, newActive);
      setPrograms(programs.map(p =>
        p.id === program.id ? { ...p, active: newActive } : p
      ));
    } catch (err) {
      console.error('Error toggling archive status:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to update program status');
    }
  };

  const resetDialogForm = () => {
    setNewProgramName("");
    setNewProgramDescription("");
    setNewProgramIntroduction("");
    setNewProgramDuration(4);
    setNewProgramWorkoutsPerWeek(3);
    setNewProgramDifficulty("beginner");
    setNewProgramGoal("general_fitness");
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const goalLabels: Record<string, string> = {
    general_fitness: 'General Fitness',
    strength: 'Strength',
    muscle_building: 'Muscle Building',
    weight_loss: 'Weight Loss',
    endurance: 'Endurance',
  };

  const difficultyLabels: Record<string, string> = {
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
  };

  const availableCatalogGoals = Array.from(new Set(catalogPrograms.map((program) => program.goal)));
  const filteredCatalogPrograms = catalogPrograms.filter(
    (program) => catalogGoalFilter === 'all' || program.goal === catalogGoalFilter
  );

  const visiblePrograms = showArchived ? programs : programs.filter(p => p.active);
  const archivedCount = programs.filter(p => !p.active).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (showExerciseManager) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Button onClick={() => setShowExerciseManager(false)}>← Back to Programs</Button>
        </Box>
        <ExerciseManager />
      </Box>
    );
  }

  if (managingProgram) {
    return (
      <Box>
        <ProgramBuilderV2
          program={managingProgram}
          onProgramUpdate={() => {
            // Refresh programs list when changes are made
            loadPrograms();
          }}
          onClose={() => setManagingProgram(null)}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Training Programs
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your weekly training programs and track your progress
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {archivedCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={showArchived ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => setShowArchived(!showArchived)}
              sx={{ height: 'fit-content' }}
            >
              {showArchived ? 'Hide' : 'Show'} Archived ({archivedCount})
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleOpenCatalog}
            sx={{ height: 'fit-content' }}
          >
            Browse Catalog
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExerciseIcon />}
            onClick={() => setShowExerciseManager(true)}
            sx={{ height: 'fit-content' }}
          >
            Manage Exercises
          </Button>
        </Box>
      </Box>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {visiblePrograms.map((program) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={program.id}>
            <Card
              sx={{
                cursor: "pointer",
                "&:hover": { elevation: 8 },
                ...(! program.active && { opacity: 0.6 }),
              }}
              onClick={() => navigate(`/planner?programId=${program.id}`)}
            >
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" component="h3">
                      {program.name}
                    </Typography>
                    {!program.active && (
                      <Chip label="Archived" size="small" color="default" />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      // Convert FrontendTrainingProgram back to TrainingProgram for management
                      const backendProgram: TrainingProgram = {
                        id: program.id,
                        name: program.name,
                        description: program.description,
                        durationWeeks: program.durationWeeks,
                        workoutsPerWeek: program.workoutsPerWeek,
                        difficulty: program.difficulty,
                        goal: program.goal,
                        active: program.active,
                        createdAt: program.createdAt.toISOString(),
                        updatedAt: program.updatedAt?.toISOString() || program.createdAt.toISOString(),
                        user: program.userIri || '',
                      };
                      setManagingProgram(backendProgram);
                    }}>
                      <ManageIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={program.active ? 'Archive' : 'Unarchive'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleArchive(program);
                      }}
                    >
                      {program.active ? <ArchiveIcon /> : <UnarchiveIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      handleStartEditProgram(program);
                    }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProgram(program.id);
                    }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Typography color="text.secondary" gutterBottom>
                  {formatDate(program.weekStartDate)} - {formatDate(program.weekEndDate)}
                </Typography>
                <Typography variant="body2">
                  {program.workoutSessions.length} workout sessions
                </Typography>
                <Typography variant="body2">
                  Total sets: {program.workoutSessions.reduce((total, session) => total + session.sets.length, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        onClick={() => setDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      <Dialog open={dialogOpen} onClose={() => {setDialogOpen(false); resetDialogForm();}} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Training Program</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Program Name"
            fullWidth
            variant="outlined"
            value={newProgramName}
            onChange={(e) => setNewProgramName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Кратко описание"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={newProgramDescription}
            onChange={(e) => setNewProgramDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <RichTextEditor
              label="Въведение (маркетингова информация за продажба онлайн)"
              value={newProgramIntroduction}
              onChange={setNewProgramIntroduction}
              placeholder="Представи програмата: за кого е, какви резултати дава, какво включва..."
              minHeight={160}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Duration (weeks)"
              type="number"
              fullWidth
              variant="outlined"
              value={newProgramDuration}
              onChange={(e) => setNewProgramDuration(Number(e.target.value))}
              inputProps={{ min: 1, max: 52 }}
            />
            <TextField
              margin="dense"
              label="Workouts per week"
              type="number"
              fullWidth
              variant="outlined"
              value={newProgramWorkoutsPerWeek}
              onChange={(e) => setNewProgramWorkoutsPerWeek(Number(e.target.value))}
              inputProps={{ min: 1, max: 7 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={newProgramDifficulty}
                label="Difficulty"
                onChange={(e) => setNewProgramDifficulty(e.target.value)}
              >
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Goal</InputLabel>
              <Select
                value={newProgramGoal}
                label="Goal"
                onChange={(e) => setNewProgramGoal(e.target.value)}
              >
                <MenuItem value="general_fitness">General Fitness</MenuItem>
                <MenuItem value="strength">Strength</MenuItem>
                <MenuItem value="muscle_building">Muscle Building</MenuItem>
                <MenuItem value="weight_loss">Weight Loss</MenuItem>
                <MenuItem value="endurance">Endurance</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setDialogOpen(false); resetDialogForm();}}>Cancel</Button>
          <Button onClick={handleCreateProgram} variant="contained">
            Create Program
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={catalogDialogOpen}
        onClose={() => setCatalogDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Program Catalog</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Goal Filter</InputLabel>
              <Select
                value={catalogGoalFilter}
                label="Goal Filter"
                onChange={(e) => setCatalogGoalFilter(String(e.target.value))}
              >
                <MenuItem value="all">All Goals</MenuItem>
                {availableCatalogGoals.map((goal) => (
                  <MenuItem key={goal} value={goal}>
                    {goalLabels[goal] || goal}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {catalogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {catalogError}
            </Alert>
          )}

          {catalogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredCatalogPrograms.map((program) => (
                <Grid size={{ xs: 12, sm: 6 }} key={program.slug}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {program.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {program.description}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Guru: <strong>{program.guru.name}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {program.guru.bio}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                        <Chip
                          size="small"
                          label={goalLabels[program.goal] || program.goal}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={difficultyLabels[program.difficulty] || program.difficulty}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${program.dayCount} days`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${program.exerciseCount} exercises`}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {program.durationWeeks} weeks • {program.workoutsPerWeek} workouts/week
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={importingSlug !== null}
                        onClick={() => handleImportCatalogProgram(program.slug)}
                      >
                        {importingSlug === program.slug ? 'Adding...' : 'Add to My Programs'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editingProgramId !== null} onClose={handleCancelEditProgram} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Training Program</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Program Name"
              fullWidth
              variant="outlined"
              value={editProgramName}
              onChange={(e) => setEditProgramName(e.target.value)}
            />
            <TextField
              margin="dense"
              label="Кратко описание"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={editProgramDescription}
              onChange={(e) => setEditProgramDescription(e.target.value)}
            />
            <Box sx={{ mt: 2 }}>
              <RichTextEditor
                label="Въведение (маркетингова информация за продажба онлайн)"
                value={editProgramIntroduction}
                onChange={setEditProgramIntroduction}
                placeholder="Представи програмата: за кого е, какви резултати дава, какво включва..."
                minHeight={160}
              />
            </Box>
            <TextField
              margin="dense"
              label="Duration (weeks)"
              type="number"
              fullWidth
              variant="outlined"
              value={editProgramDuration}
              onChange={(e) => setEditProgramDuration(Number(e.target.value))}
              inputProps={{ min: 1, max: 52 }}
            />
            <TextField
              margin="dense"
              label="Workouts per week"
              type="number"
              fullWidth
              variant="outlined"
              value={editProgramWorkoutsPerWeek}
              onChange={(e) => setEditProgramWorkoutsPerWeek(Number(e.target.value))}
              inputProps={{ min: 1, max: 7 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControl fullWidth margin="dense">
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={editProgramDifficulty}
                label="Difficulty"
                onChange={(e) => setEditProgramDifficulty(e.target.value)}
              >
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Goal</InputLabel>
              <Select
                value={editProgramGoal}
                label="Goal"
                onChange={(e) => setEditProgramGoal(e.target.value)}
              >
                <MenuItem value="general_fitness">General Fitness</MenuItem>
                <MenuItem value="strength">Strength</MenuItem>
                <MenuItem value="muscle_building">Muscle Building</MenuItem>
                <MenuItem value="weight_loss">Weight Loss</MenuItem>
                <MenuItem value="endurance">Endurance</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEditProgram}>Cancel</Button>
          <Button onClick={handleSaveEditProgram} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingPrograms;
